import type { IPaymentMethod } from '@root/query/subscriptions'
import type { TFunction } from 'i18next'
import type { IconType } from 'react-icons'
import {
  FaCcAmex,
  FaCcMastercard,
  FaCcVisa,
  FaCreditCard,
  FaLink,
  FaPaypal,
  FaWallet
} from 'react-icons/fa'
import { FaBuildingColumns } from 'react-icons/fa6'

// Card brands relevant for the Finnish and German markets. Rarer brands
// (Discover, Diners, JCB, UnionPay, ...) fall back to the generic card icon.
const CARD_BRAND_ICONS: Record<string, IconType> = {
  visa: FaCcVisa,
  mastercard: FaCcMastercard,
  amex: FaCcAmex
}

// Non-card methods relevant for the Finnish and German markets. Anything else
// falls back to the generic wallet icon.
const PAYMENT_METHOD_TYPE_ICONS: Record<string, IconType> = {
  sepa_debit: FaBuildingColumns,
  paypal: FaPaypal,
  link: FaLink
}

export const isCardPaymentMethod = (
  paymentMethod: IPaymentMethod
): paymentMethod is IPaymentMethod & {
  brand: string
  last4: string
  exp_month: number
  exp_year: number
} => paymentMethod.type === 'card' && Boolean(paymentMethod.brand && paymentMethod.last4)

const getCardBrandIcon = (brand: string): IconType =>
  CARD_BRAND_ICONS[brand.toLowerCase()] ?? FaCreditCard

export const getPaymentMethodIcon = (paymentMethod: IPaymentMethod): IconType => {
  if (isCardPaymentMethod(paymentMethod)) {
    return getCardBrandIcon(paymentMethod.brand)
  }

  return PAYMENT_METHOD_TYPE_ICONS[paymentMethod.type] ?? FaWallet
}

const humanizePaymentMethodType = (type: string): string =>
  type
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

export const getPaymentMethodTypeLabel = (paymentMethod: IPaymentMethod, t: TFunction): string =>
  t(`settings.billing.paymentMethodTypes.${paymentMethod.type}`, {
    defaultValue: humanizePaymentMethodType(paymentMethod.type)
  })

export const getPaymentMethodTitle = (paymentMethod: IPaymentMethod, t: TFunction): string => {
  if (isCardPaymentMethod(paymentMethod)) {
    return t('settings.billing.paymentMethodMasked', { last4: paymentMethod.last4 })
  }

  return getPaymentMethodTypeLabel(paymentMethod, t)
}

export const getPaymentMethodDetail = (
  paymentMethod: IPaymentMethod,
  t: TFunction
): string | null => {
  if (isCardPaymentMethod(paymentMethod)) {
    return t('settings.billing.paymentMethodExpires', {
      month: String(paymentMethod.exp_month).padStart(2, '0'),
      year: paymentMethod.exp_year
    })
  }

  return null
}
